import { useState } from "react";
import { Download, LoaderCircle } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { getDocumentDownloadUrl } from "@/lib/api/documents";
import { downloadRfiFilesArchive } from "@/lib/api/rfi";
import type { RFIRequestFile } from "@/types/backend";

export function RfiFileDownload({ projectId, requestId, files }: {
  projectId: string;
  requestId: string;
  files: readonly RFIRequestFile[];
}) {
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      if (files.length === 1) {
        const { url } = await getDocumentDownloadUrl(projectId, files[0].id);
        const link = document.createElement("a");
        link.href = url;
        link.download = files[0].filename;
        link.rel = "noopener";
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        await downloadRfiFilesArchive(projectId, requestId);
      }
    } catch {
      toast({ title: "Download failed", description: "Please try again.", variant: "destructive" });
    } finally {
      setDownloading(false);
    }
  };

  if (files.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <button
      type="button"
      aria-label="Download"
      title="Download"
      aria-busy={downloading}
      disabled={downloading}
      onClick={() => void download()}
      className="inline-flex rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
    >
      {downloading
        ? <LoaderCircle aria-hidden="true" className="h-4 w-4 animate-spin" />
        : <Download aria-hidden="true" className="h-4 w-4" />}
    </button>
  );
}
